<template>
    <div>
        <app-dialog
            :value="value"
            size="lg"
            append-to-body
            @input="$emit('input', $event)"
        >
            <template #header>
                <h2 class="text-lg font-semibold text-theme-base">
                    Historial de versiones
                </h2>
                <p class="mt-1 text-sm text-theme-muted">
                    Guardamos una copia de tu diseño antes de cada cambio de
                    modo, bloques o colores. Conservamos las últimas 15.
                </p>
            </template>

            <div class="flex flex-col gap-4 md:flex-row md:items-start">
                <div
                    v-if="!isMobileDetailOnly"
                    class="flex w-full flex-col gap-3 md:w-72 md:flex-shrink-0"
                >
                    <div
                        v-if="isLoadingList"
                        class="flex justify-center py-10 text-theme-muted"
                    >
                        Cargando…
                    </div>

                    <div
                        v-else-if="!versions.length"
                        class="flex flex-col items-center gap-2 rounded-lg border border-dashed border-theme-border px-4 py-10 text-center text-sm text-theme-muted"
                    >
                        <clock-icon class="h-8 w-8 opacity-60" />
                        Aún no hay versiones. Se guarda una copia automática
                        cada vez que cambias el modo, los bloques o los colores.
                    </div>

                    <button
                        v-for="version in versions"
                        :key="version.id"
                        type="button"
                        class="flex flex-col gap-2 rounded-lg border px-3 py-2.5 text-left transition"
                        :class="
                            version.id === selectedVersionId
                                ? 'border-theme-primary bg-theme-hover'
                                : 'border-theme-border hover:bg-theme-hover'
                        "
                        @click="selectVersion(version.id)"
                    >
                        <span class="text-sm font-medium text-theme-base">
                            <template v-if="version.restorableToday === false"
                                >🔒
                            </template>
                            {{ triggerTitle(version) }}
                        </span>
                        <span class="text-xs text-theme-muted">
                            <span :title="absoluteDate(version.createdAt)">
                                {{ formatDate(version.createdAt) }}
                            </span>
                            <template v-if="version.createdBy">
                                · {{ version.createdBy.name }}
                            </template>
                        </span>
                        <span class="flex items-center gap-2">
                            <display-mode-silhouette
                                class="h-5 w-7 flex-shrink-0 text-theme-primary"
                                :shapes="
                                    displayModeOption(
                                        version.summary.displayMode
                                    ).silhouette
                                "
                            />
                            <span class="text-xs text-theme-muted">
                                {{
                                    displayModeOption(
                                        version.summary.displayMode
                                    ).label
                                }}
                                · {{ version.summary.segmentsCount }}
                                {{
                                    version.summary.segmentsCount === 1
                                        ? "bloque"
                                        : "bloques"
                                }}
                            </span>
                            <span class="ml-auto flex gap-1">
                                <span
                                    v-for="key in SWATCH_KEYS"
                                    :key="key"
                                    class="h-3 w-3 rounded-full border border-theme-border"
                                    :style="{
                                        backgroundColor:
                                            version.summary.stackColors[key]
                                    }"
                                ></span>
                            </span>
                        </span>
                    </button>

                    <button
                        v-if="canCreateManual"
                        type="button"
                        class="mt-1 flex items-center justify-center gap-1.5 rounded-lg border border-theme-border px-3 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                        @click="openManualModal"
                    >
                        🔖 Guardar versión actual
                    </button>
                </div>

                <div
                    v-if="showDetailColumn"
                    class="flex min-w-0 flex-1 flex-col gap-3"
                >
                    <button
                        v-if="isMobileDetailOnly"
                        type="button"
                        class="flex items-center gap-1 self-start text-sm font-medium text-theme-primary"
                        @click="backToList"
                    >
                        <chevron-left-icon class="h-4 w-4" />
                        Volver a la lista
                    </button>

                    <div
                        v-if="isLoadingDetail"
                        class="flex justify-center py-10 text-theme-muted"
                    >
                        Cargando…
                    </div>

                    <template v-else-if="selectedVersion">
                        <div>
                            <h3 class="text-base font-semibold text-theme-base">
                                {{ triggerTitle(selectedVersion) }}
                            </h3>
                            <p class="text-xs text-theme-muted">
                                {{ absoluteDate(selectedVersion.createdAt) }}
                                <template v-if="selectedVersion.createdBy">
                                    · {{ selectedVersion.createdBy.name }}
                                </template>
                            </p>
                        </div>

                        <div class="flex gap-4">
                            <div
                                class="w-28 flex-shrink-0 rounded-lg border border-theme-border bg-theme-canvas py-3"
                            >
                                <display-mode-silhouette
                                    class="mx-auto h-24 w-20 text-theme-primary"
                                    :shapes="
                                        displayModeOption(
                                            selectedVersion.snapshot.displayMode
                                        ).preview
                                    "
                                    :view-box="previewViewBox"
                                />
                            </div>
                            <div class="flex flex-1 flex-col gap-1 text-sm">
                                <p>
                                    <span class="text-theme-muted">Modo:</span>
                                    {{
                                        displayModeOption(
                                            selectedVersion.snapshot.displayMode
                                        ).label
                                    }}
                                </p>
                                <p
                                    v-for="entry in segmentsByTypeEntries"
                                    :key="entry.type"
                                >
                                    <span class="text-theme-muted"
                                        >{{ entry.label }}:</span
                                    >
                                    {{ entry.count }}
                                </p>
                                <p class="flex items-center gap-1">
                                    <span class="text-theme-muted"
                                        >Colores:</span
                                    >
                                    <span
                                        v-for="key in SWATCH_KEYS"
                                        :key="key"
                                        :title="
                                            selectedVersion.snapshot
                                                .stackColors[key]
                                        "
                                        class="h-4 w-4 rounded-full border border-theme-border"
                                        :style="{
                                            backgroundColor:
                                                selectedVersion.snapshot
                                                    .stackColors[key]
                                        }"
                                    ></span>
                                </p>
                                <p v-if="selectedUsesSearchBar">
                                    <span class="text-theme-muted"
                                        >Buscador:</span
                                    >
                                    {{
                                        selectedVersion.snapshot.showSearchBar
                                            ? "sí"
                                            : "no"
                                    }}
                                </p>
                            </div>
                        </div>

                        <div
                            v-if="hasMissingReferences"
                            class="rounded-lg bg-theme-warning px-3 py-2 text-sm text-theme-inverse"
                        >
                            {{ missingReferencesText }}
                        </div>

                        <div
                            v-if="designDiff"
                            class="rounded-lg border border-theme-border bg-theme-canvas px-3 py-2 text-sm"
                        >
                            <p class="mb-1 font-medium text-theme-base">
                                Si restauras esta versión
                            </p>
                            <p v-if="designDiff.displayModeChanged">
                                Modo: {{ currentDisplayModeLabel }} →
                                {{ selectedDisplayModeLabel }}
                            </p>
                            <p v-if="designDiff.segmentsDelta !== 0">
                                Bloques: {{ currentSegmentsCount }} →
                                {{ selectedVersion.snapshot.segments.length }}
                                <template v-if="designDiff.segmentsDelta > 0">
                                    (recuperas {{ designDiff.segmentsDelta }})
                                </template>
                                <template v-else>
                                    (quitas {{ -designDiff.segmentsDelta }})
                                </template>
                            </p>
                            <p v-if="designDiff.colorsChanged">
                                Colores: cambian
                            </p>
                            <p v-if="designDiff.searchBarChanged">
                                Buscador: cambia
                            </p>
                            <p v-if="!hasDiff" class="text-theme-muted">
                                Es igual a tu diseño actual.
                            </p>
                        </div>

                        <div
                            v-if="canRestore && selectedLock"
                            class="flex flex-col gap-2 text-sm"
                        >
                            <p class="text-theme-base">
                                🔒
                                <template v-if="selectedLock.kind === 'plan'">
                                    Pásate a un plan superior para recuperar
                                    este diseño.
                                </template>
                                <template v-else>
                                    Activa Productos para recuperar este diseño.
                                </template>
                            </p>
                            <app-button
                                v-if="canShowSelectedUpgrade"
                                color="brand-blue"
                                @click="openUpgradeForSelected"
                            >
                                Ver planes
                            </app-button>
                        </div>

                        <app-button
                            v-else-if="canRestore"
                            color="success"
                            :disabled="!hasDiff"
                            @click="openRestoreConfirm"
                        >
                            {{
                                hasDiff
                                    ? "Restaurar esta versión"
                                    : "Es igual a tu diseño actual"
                            }}
                        </app-button>
                    </template>
                </div>
            </div>
        </app-dialog>

        <catalog-restore-confirm-modal
            v-model="restoreConfirmOpen"
            :version="selectedVersion"
            :is-saving="isRestoring"
            @confirm="confirmRestore"
        />

        <app-dialog v-model="manualModalOpen" size="sm" append-to-body>
            <template #header>
                <h3 class="text-lg font-semibold text-theme-base">
                    Guardar versión actual
                </h3>
            </template>

            <div>
                <label class="mb-1 block text-sm text-theme-muted"
                    >Nombre (opcional)</label
                >
                <input
                    v-model="manualLabel"
                    type="text"
                    maxlength="60"
                    placeholder="Antes del lanzamiento"
                    class="w-full rounded-lg border border-theme-border bg-theme-input px-3 py-2 text-sm text-theme-base focus:outline-none focus:ring-2 focus:ring-theme-primary"
                />
            </div>

            <template #footer>
                <app-button
                    class="w-full"
                    color="success"
                    :loading="isSavingManual"
                    @click="confirmManual"
                >
                    Guardar
                </app-button>
            </template>
        </app-dialog>
    </div>
</template>

<script>
import { DateTime } from "luxon";
import { mapActions, mapState } from "vuex";

import {
    DISPLAY_MODE_PREVIEW_VIEW_BOX,
    displayModeUsesSearchBar,
    findDisplayModeOption
} from "@/common/fields/display-modes";
import {
    DISPLAY_MODE_STATES,
    canShowUpgrade,
    displayModeLockFeature,
    displayModeState
} from "@/common/fields/plan-features";
import { isCatalogVersionLockedError } from "@/common/lib/formatter";

import DisplayModeSilhouette from "@/components/editor/DisplayModeSilhouette.vue";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";
import ClockIcon from "@/components/icons/ClockIcon.vue";
import AppButton from "@/components/utils/AppButton.vue";
import AppDialog from "@/components/utils/AppDialog.vue";

import CatalogRestoreConfirmModal from "./CatalogRestoreConfirmModal.vue";

const SWATCH_KEYS = ["primary", "emphasis", "background", "text"];

const TRIGGER_TITLES = {
    segments: "Antes de guardar bloques",
    colors: "Antes de cambiar colores",
    restore: "Antes de restaurar",
    "plan-downgrade": "Antes de bajar de plan"
};

const SEGMENT_TYPE_LABELS = {
    links: "Enlaces",
    banners: "Banners",
    lines: "Líneas",
    productsCarousel: "Productos",
    text: "Texto",
    testimonials: "Testimonios",
    location: "Ubicación",
    media: "Redes"
};

export default {
    name: "CatalogHistoryModal",
    components: {
        AppButton,
        AppDialog,
        CatalogRestoreConfirmModal,
        ChevronLeftIcon,
        ClockIcon,
        DisplayModeSilhouette
    },
    props: {
        value: { type: Boolean, default: false },
        catalog: { type: Object, default: null }
    },
    data() {
        return {
            SWATCH_KEYS,
            versions: [],
            isLoadingList: false,
            selectedVersionId: null,
            selectedVersion: null,
            isLoadingDetail: false,
            isDesktop: true,
            restoreConfirmOpen: false,
            isRestoring: false,
            manualModalOpen: false,
            manualLabel: "",
            isSavingManual: false
        };
    },
    computed: {
        ...mapState("auth", {
            planPermissions: "permissions",
            featureFlags: "featureFlags"
        }),
        selectedUsesSearchBar() {
            return displayModeUsesSearchBar(
                this.selectedVersion?.snapshot.displayMode
            );
        },
        selectedLock() {
            const version = this.selectedVersion;
            if (!version || version.restorableToday !== false) return null;
            const displayMode = version.snapshot.displayMode;
            const state = displayModeState(
                displayMode,
                this.planPermissions,
                this.featureFlags
            );
            if (state === DISPLAY_MODE_STATES.needsProducts) {
                return { kind: "needs-products" };
            }
            return {
                kind: "plan",
                feature: displayModeLockFeature(
                    displayMode,
                    this.planPermissions
                )
            };
        },
        // El aviso sale siempre (el bloqueo es real, del backend); el botón,
        // solo con el `upgrade:Feature<X>` de esa vista.
        canShowSelectedUpgrade() {
            return (
                this.selectedLock?.kind === "plan" &&
                canShowUpgrade(this.planPermissions, this.selectedLock.feature)
            );
        },
        canCreateManual() {
            return this.$ability.hasPermission("catalog:AddHistoryItem");
        },
        canRestore() {
            return this.$ability.hasPermission("catalog:RestoreHistory");
        },
        isMobileDetailOnly() {
            return !this.isDesktop && !!this.selectedVersionId;
        },
        showDetailColumn() {
            return this.isDesktop || !!this.selectedVersionId;
        },
        previewViewBox() {
            return DISPLAY_MODE_PREVIEW_VIEW_BOX;
        },
        currentDisplayModeLabel() {
            return this.displayModeOption(this.catalog?.displayMode).label;
        },
        selectedDisplayModeLabel() {
            return this.displayModeOption(
                this.selectedVersion?.snapshot.displayMode
            ).label;
        },
        currentSegmentsCount() {
            return this.catalog?.segments?.length ?? 0;
        },
        segmentsByTypeEntries() {
            if (!this.selectedVersion) return [];
            return Object.entries(
                this.selectedVersion.summary.segmentsByType
            ).map(([type, count]) => ({
                type,
                count,
                label: SEGMENT_TYPE_LABELS[type] ?? type
            }));
        },
        hasMissingReferences() {
            const missing = this.selectedVersion?.missingReferences;
            if (!missing) return false;
            return (
                missing.links > 0 || missing.products > 0 || missing.lines > 0
            );
        },
        missingReferencesText() {
            const missing = this.selectedVersion?.missingReferences;
            if (!missing) return "";
            const parts = [];
            if (missing.links) {
                parts.push(
                    `${missing.links} ${
                        missing.links === 1 ? "enlace" : "enlaces"
                    }`
                );
            }
            if (missing.products) {
                parts.push(
                    `${missing.products} ${
                        missing.products === 1 ? "producto" : "productos"
                    }`
                );
            }
            if (missing.lines) {
                parts.push(
                    `${missing.lines} ${
                        missing.lines === 1 ? "línea" : "líneas"
                    }`
                );
            }
            return `${parts.join(", ")} de esta versión ya no ${
                parts.length === 1 && missing.links === 1 ? "existe" : "existen"
            } y no se ${
                parts.length === 1 && missing.links === 1
                    ? "mostrará"
                    : "mostrarán"
            }.`;
        },
        designDiff() {
            if (!this.selectedVersion || !this.catalog) return null;
            const snapshot = this.selectedVersion.snapshot;
            return {
                displayModeChanged:
                    snapshot.displayMode !== this.catalog.displayMode,
                segmentsDelta:
                    snapshot.segments.length - this.currentSegmentsCount,
                colorsChanged:
                    JSON.stringify(snapshot.stackColors) !==
                    JSON.stringify(this.catalog.stackColors ?? {}),
                searchBarChanged:
                    displayModeUsesSearchBar(snapshot.displayMode) &&
                    Boolean(snapshot.showSearchBar) !==
                        Boolean(this.catalog.showSearchBar)
            };
        },
        hasDiff() {
            if (!this.designDiff) return false;
            return (
                this.designDiff.displayModeChanged ||
                this.designDiff.segmentsDelta !== 0 ||
                this.designDiff.colorsChanged ||
                this.designDiff.searchBarChanged
            );
        }
    },
    watch: {
        value(isOpen) {
            if (isOpen) this.onOpen();
        }
    },
    mounted() {
        if (this.value) this.onOpen();
    },
    methods: {
        ...mapActions("tenants", [
            "listCatalogVersions",
            "getCatalogVersion",
            "createCatalogVersion",
            "restoreCatalogVersion"
        ]),
        ...mapActions("platformSubscriptions", ["openPlanUpgrade"]),
        openUpgradeForSelected() {
            this.openPlanUpgrade({
                kind: "feature",
                feature: this.selectedLock.feature
            });
        },
        displayModeOption(value) {
            return (
                findDisplayModeOption(value) ?? {
                    label: value ?? "",
                    silhouette: [],
                    preview: []
                }
            );
        },
        onOpen() {
            this.isDesktop =
                typeof window !== "undefined" &&
                typeof window.matchMedia === "function" &&
                window.matchMedia("(min-width: 768px)").matches;
            this.selectedVersionId = null;
            this.selectedVersion = null;
            this.loadVersions();
        },
        async loadVersions() {
            this.isLoadingList = true;
            try {
                this.versions = await this.listCatalogVersions();
                if (this.isDesktop && this.versions.length) {
                    this.selectVersion(this.versions[0].id);
                }
            } catch (error) {
                this.$sound.play("error");
                await this.$swal.fire({
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            } finally {
                this.isLoadingList = false;
            }
        },
        async selectVersion(id) {
            this.selectedVersionId = id;
            this.selectedVersion = null;
            this.isLoadingDetail = true;
            try {
                this.selectedVersion = await this.getCatalogVersion({
                    id
                });
            } catch (error) {
                this.$sound.play("error");
                await this.$swal.fire({
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            } finally {
                this.isLoadingDetail = false;
            }
        },
        backToList() {
            this.selectedVersionId = null;
            this.selectedVersion = null;
        },
        openRestoreConfirm() {
            this.restoreConfirmOpen = true;
        },
        async confirmRestore() {
            this.isRestoring = true;
            try {
                await this.restoreCatalogVersion({
                    id: this.selectedVersionId
                });
                this.isRestoring = false;
                this.restoreConfirmOpen = false;
                this.$emit("input", false);
                this.$emit("restored");
                this.$sound.play("success");
                await this.$swal.fire({
                    title: "Diseño restaurado",
                    icon: "success",
                    timer: 1800,
                    showConfirmButton: false
                });
            } catch (error) {
                this.isRestoring = false;
                const lockedFeature = isCatalogVersionLockedError(error)
                    ? displayModeLockFeature(
                          error.data?.displayMode,
                          this.planPermissions
                      )
                    : null;
                if (lockedFeature) {
                    this.restoreConfirmOpen = false;
                    this.openPlanUpgrade({
                        kind: "feature",
                        feature: lockedFeature
                    });
                    await this.loadVersions();
                    return;
                }
                this.$sound.play("error");
                await this.$swal.fire({
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        openManualModal() {
            this.manualLabel = "";
            this.manualModalOpen = true;
        },
        async confirmManual() {
            this.isSavingManual = true;
            try {
                const result = await this.createCatalogVersion({
                    label: this.manualLabel.trim() || undefined
                });
                this.versions.unshift(result);
                this.isSavingManual = false;
                this.manualModalOpen = false;
            } catch (error) {
                this.isSavingManual = false;
                if (error.statusCode === 409) {
                    await this.$swal.fire({
                        title: "Ya está guardado",
                        text: "Tu diseño actual ya está guardado como la versión más reciente.",
                        icon: "info"
                    });
                    return;
                }
                this.$sound.play("error");
                await this.$swal.fire({
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        triggerTitle(version) {
            if (version.trigger === "display-mode") {
                const label = this.displayModeOption(
                    version.details?.displayModeTo
                ).label;
                return `Antes de cambiar a ${label}`;
            }
            if (version.trigger === "manual") {
                return version.label
                    ? `🔖 "${version.label}"`
                    : "🔖 Versión guardada a mano";
            }
            return TRIGGER_TITLES[version.trigger] ?? version.trigger;
        },
        formatDate(iso) {
            const date = DateTime.fromISO(iso).setLocale("es");
            const now = DateTime.now();
            if (date.hasSame(now, "day")) {
                return date.toRelative({ locale: "es" });
            }
            if (date.hasSame(now.minus({ days: 1 }), "day")) {
                return `ayer ${date.toFormat("HH:mm")}`;
            }
            return date.toFormat("d MMM");
        },
        absoluteDate(iso) {
            return DateTime.fromISO(iso)
                .setLocale("es")
                .toFormat("d 'de' LLLL yyyy, HH:mm");
        }
    }
};
</script>
